import { useMemo, useState } from 'react';

// Sprint 11 UX.F3 — Token diff review UI.
//
// Rendered after the user re-imports a refined Claude Design bundle and
// the API returns a TokenDiffReport. The PM toggles individual changes
// (default = all accepted), reviews breaking-change warnings + affected
// components, and clicks "Apply" to commit a new design_tokens version.
//
// Issue #581 AC: "Approval updates the canonical token file and commits
// to the project repo." We satisfy the approval half here; the commit
// half happens in the apply endpoint.

interface TokenChange {
  path:           string;
  category:       string;
  oldValue:       string | number | boolean | null;
  newValue:       string | number | boolean | null;
  changeType:     'added' | 'modified' | 'removed';
  impact:         'high' | 'medium' | 'low';
  breaking:       boolean;
  breakingReason: string;
}

export interface TokenDiffReport {
  totalChanges:       number;
  added:              TokenChange[];
  modified:           TokenChange[];
  removed:            TokenChange[];
  breakingChanges:    TokenChange[];
  affectedComponents: string[];
  summary:            string;
  diffHash:           string;
}

interface Props {
  diff:    TokenDiffReport;
  /** Names + IDs for affected components, looked up from the catalogue. */
  affectedComponentLabels?: { id: string; name: string }[];
  onApply:  (excludePaths: string[]) => void | Promise<void>;
  onCancel: () => void;
  applying?: boolean;
}

export default function TokenDiffReview({ diff, affectedComponentLabels, onApply, onCancel, applying = false }: Props) {
  const allPaths = useMemo(
    () => [...diff.modified, ...diff.added, ...diff.removed].map(c => c.path),
    [diff.modified, diff.added, diff.removed],
  );

  // Default = accept everything; user unchecks to exclude. Matches the
  // F2 BundleMappingApproval ergonomics.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  const togglePath = (path: string) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  const acceptedCount = allPaths.length - excluded.size;

  // Group modifications by category for the section render.
  const modifiedByCategory = useMemo(() => {
    const out: Record<string, TokenChange[]> = {};
    for (const c of diff.modified) {
      if (!out[c.category]) out[c.category] = [];
      out[c.category].push(c);
    }
    return out;
  }, [diff.modified]);

  return (
    <div style={{ padding: 16, borderRadius: 'var(--radius-md)', background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: '0 0 4px' }}>
        Design Token Changes ({diff.totalChanges} {diff.totalChanges === 1 ? 'change' : 'changes'})
      </h3>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
        {diff.summary}
      </p>

      {/* Breaking changes — surfaced first, big warning banner. */}
      {diff.breakingChanges.length > 0 && (
        <div role="alert" style={{ padding: 12, borderRadius: 'var(--radius-sm)', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', margin: '0 0 16px' }}>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, color: 'var(--warning)', margin: '0 0 8px' }}>
            ⚠ {diff.breakingChanges.length} breaking {diff.breakingChanges.length === 1 ? 'change' : 'changes'}
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>
            {diff.breakingChanges.map(c => (
              <li key={c.path}>
                <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{c.path}</code>
                {c.changeType !== 'added'  && <> : <ValuePreview value={c.oldValue} category={c.category} /></>}
                {c.changeType === 'modified' && <> → </>}
                {c.changeType !== 'removed' && <ValuePreview value={c.newValue} category={c.category} />}
                <span style={{ display: 'block', fontSize: 11, opacity: 0.85 }}>{c.breakingReason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {Object.entries(modifiedByCategory).map(([category, items]) => (
        <ChangeSection
          key={category}
          title={`${capitalise(category)}${items.length > 1 ? 's' : ''} Modified`}
          changes={items}
          excluded={excluded}
          onToggle={togglePath}
          disabled={applying}
        />
      ))}

      {diff.added.length > 0 && (
        <ChangeSection
          title={`Tokens Added`}
          changes={diff.added}
          excluded={excluded}
          onToggle={togglePath}
          disabled={applying}
        />
      )}

      {diff.removed.length > 0 && (
        <ChangeSection
          title={`Tokens Removed`}
          changes={diff.removed}
          excluded={excluded}
          onToggle={togglePath}
          disabled={applying}
        />
      )}

      {affectedComponentLabels && affectedComponentLabels.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: '0 0 8px' }}>
            Affected Components ({affectedComponentLabels.length})
          </h4>
          <ul style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0, paddingLeft: 18 }}>
            {affectedComponentLabels.map(c => (
              <li key={c.id}>{c.name} <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>({c.id})</code></li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={applying}
          style={{ fontFamily: 'var(--font-body)', fontSize: 13, padding: '6px 14px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', cursor: applying ? 'not-allowed' : 'pointer' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void onApply([...excluded])}
          disabled={applying || acceptedCount === 0}
          style={{
            fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600,
            padding: '6px 14px', color: 'white',
            background: acceptedCount === 0 ? 'var(--text-secondary)' : 'var(--primary)',
            border: 'none', borderRadius: 'var(--radius-sm)',
            cursor: applying ? 'progress' : (acceptedCount === 0 ? 'not-allowed' : 'pointer'),
            opacity: applying ? 0.6 : 1,
          }}
        >
          {applying ? 'Applying…' : `Apply ${acceptedCount} ${acceptedCount === 1 ? 'Change' : 'Changes'}`}
        </button>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

interface ChangeSectionProps {
  title:    string;
  changes:  TokenChange[];
  excluded: Set<string>;
  onToggle: (path: string) => void;
  disabled: boolean;
}

function ChangeSection({ title, changes, excluded, onToggle, disabled }: ChangeSectionProps) {
  if (changes.length === 0) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: '0 0 8px' }}>
        {title} ({changes.length})
      </h4>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {changes.map(c => (
          <li key={c.path} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', fontFamily: 'var(--font-body)', fontSize: 13 }}>
            <input
              type="checkbox"
              id={`tdr-${c.path}`}
              checked={!excluded.has(c.path)}
              onChange={() => onToggle(c.path)}
              disabled={disabled}
            />
            <label htmlFor={`tdr-${c.path}`} style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
              <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-secondary)' }}>{c.path}</code>
              {c.changeType !== 'added' && <ValuePreview value={c.oldValue} category={c.category} />}
              {c.changeType === 'modified' && <span style={{ color: 'var(--text-secondary)' }}>→</span>}
              {c.changeType !== 'removed' && <ValuePreview value={c.newValue} category={c.category} />}
              {c.impact === 'high'   && <ImpactBadge impact="high" />}
              {c.impact === 'medium' && <ImpactBadge impact="medium" />}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ValuePreview({ value, category }: { value: TokenChange['oldValue']; category: string }) {
  if (value === null || value === undefined) {
    return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontStyle: 'italic', color: 'var(--text-secondary)' }}>—</span>;
  }
  const stringValue = String(value);
  // Render colour swatches when the value looks like a hex.
  const isHex = category === 'color' && /^#[0-9A-Fa-f]{3,8}$/.test(stringValue);
  if (isHex) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} data-testid="token-swatch">
        <span
          aria-hidden
          style={{ width: 14, height: 14, borderRadius: 3, background: stringValue, border: '1px solid var(--border)', display: 'inline-block' }}
        />
        <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text)' }}>{stringValue}</code>
      </span>
    );
  }
  return <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text)' }}>{stringValue}</code>;
}

function ImpactBadge({ impact }: { impact: 'high' | 'medium' }) {
  const colour = impact === 'high' ? 'var(--warning)' : 'var(--text-secondary)';
  const label  = impact === 'high' ? 'high impact' : 'medium';
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: colour, padding: '1px 6px', borderRadius: 'var(--radius-sm)', border: `1px solid ${colour}` }}>
      {label}
    </span>
  );
}

function capitalise(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}
